import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, FileText, GripVertical, ToggleLeft } from 'lucide-react';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text (free-form answer)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple Choice' }
];

export default function ConsultationFormsManager({ services = [] }) {
  const [forms, setForms] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formName, setFormName] = useState('');
  const [formServiceIds, setFormServiceIds] = useState([]);
  const [questions, setQuestions] = useState([]);

  useEffect(() => { fetchForms(); }, []);

  const fetchForms = async () => {
    try {
      const res = await api.get('/consultation-forms');
      setForms(res.data || []);
    } catch (err) { console.error('Failed to load forms:', err); }
  };

  const openCreate = () => {
    setEditingForm(null);
    setFormName('');
    setFormServiceIds([]);
    setQuestions([{ text: '', type: 'text', options: [], required: true }]);
    setDialogOpen(true);
  };

  const openEdit = (form) => {
    setEditingForm(form);
    setFormName(form.name);
    setFormServiceIds(form.service_ids || []);
    setQuestions(form.questions.map(q => ({ ...q, options: q.options || [] })));
    setDialogOpen(true);
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, { text: '', type: 'text', options: [], required: true }]);
  };

  const removeQuestion = (idx) => {
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  const addOption = (idx) => {
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, options: [...q.options, ''] } : q));
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const newOptions = [...q.options];
      newOptions[oIdx] = value;
      return { ...q, options: newOptions };
    }));
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions(prev => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return { ...q, options: q.options.filter((_, j) => j !== oIdx) };
    }));
  };

  const toggleFormService = (serviceId) => {
    setFormServiceIds(prev =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]
    );
  };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Form name is required'); return; }
    if (questions.length === 0) { toast.error('Add at least one question'); return; }
    const validQ = questions.filter(q => q.text.trim());
    if (validQ.length === 0) { toast.error('Questions must have text'); return; }

    try {
      const payload = {
        name: formName.trim(),
        questions: validQ.map(q => ({
          ...q,
          question_id: q.question_id || '',
          options: q.type === 'multiple_choice' ? q.options.filter(o => o.trim()) : []
        })),
        service_ids: formServiceIds
      };

      if (editingForm) {
        const res = await api.put(`/consultation-forms/${editingForm.form_id}`, payload);
        setForms(prev => prev.map(f => f.form_id === editingForm.form_id ? res.data : f));
        toast.success('Form updated');
      } else {
        const res = await api.post('/consultation-forms', payload);
        setForms(prev => [...prev, res.data]);
        toast.success('Form created');
      }
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (formId) => {
    try {
      await api.delete(`/consultation-forms/${formId}`);
      setForms(prev => prev.filter(f => f.form_id !== formId));
      toast.success('Form deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#D4AF37]" />
          Consultation Forms
        </h3>
        <Button onClick={openCreate} className="btn-primary gap-1" size="sm" data-testid="create-form-btn">
          <Plus className="h-4 w-4" /> New Form
        </Button>
      </div>

      {forms.length === 0 ? (
        <div className="text-center py-6 text-stone-500 text-sm">
          <p>No consultation forms yet</p>
          <p className="text-xs text-stone-400 mt-1">Create pre-appointment forms to collect allergy info, preferences, and more</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="forms-list">
          {forms.map(form => (
            <div key={form.form_id} className="p-4 bg-[#F9F6F0] rounded-lg" data-testid={`form-${form.form_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-[#1A1A1A] text-sm">{form.name}</h4>
                  <p className="text-xs text-stone-500 mt-0.5">
                    {form.questions?.length || 0} question{form.questions?.length !== 1 ? 's' : ''}
                    {form.service_ids?.length > 0
                      ? ` · Assigned to ${form.service_ids.length} service${form.service_ids.length > 1 ? 's' : ''}`
                      : ' · All services'
                    }
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.questions?.map((q, qIdx) => (
                      <span key={q.text || qIdx} className="text-xs px-2 py-0.5 bg-white rounded border border-[#E6E4DD] text-stone-600 truncate max-w-[200px]">
                        {q.text}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1 ml-3 flex-shrink-0">
                  <button onClick={() => openEdit(form)} className="p-1.5 hover:bg-white rounded transition-colors">
                    <Edit className="h-3.5 w-3.5 text-stone-500" />
                  </button>
                  <button onClick={() => handleDelete(form.form_id)} className="p-1.5 hover:bg-red-50 rounded transition-colors">
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingForm ? 'Edit Form' : 'New Consultation Form'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <Label htmlFor="form-name">Form Name *</Label>
              <Input id="form-name" value={formName} onChange={e => setFormName(e.target.value)}
                placeholder='e.g. "Hair Service Consultation"' className="input-zuriya" data-testid="form-name-input" />
            </div>

            <div>
              <Label className="mb-2 block">Questions</Label>
              <div className="space-y-4">
                {questions.map((q, idx) => (
                  <div key={q.question_id || `q-${idx}`} className="p-3 bg-[#F9F6F0] rounded-lg space-y-2" data-testid={`question-${idx}`}>
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-medium text-[#D4AF37] bg-white px-2 py-1 rounded mt-0.5">Q{idx + 1}</span>
                      <div className="flex-1 space-y-2">
                        <Input value={q.text} onChange={e => updateQuestion(idx, 'text', e.target.value)}
                          placeholder="Enter your question" className="input-zuriya text-sm" data-testid={`question-text-${idx}`} />
                        <div className="flex items-center gap-3">
                          <Select value={q.type} onValueChange={val => updateQuestion(idx, 'type', val)}>
                            <SelectTrigger className="input-zuriya text-xs w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {QUESTION_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <Checkbox checked={q.required} onCheckedChange={val => updateQuestion(idx, 'required', val)} />
                            Required
                          </label>
                        </div>
                        {q.type === 'multiple_choice' && (
                          <div className="space-y-1 pl-2">
                            {q.options.map((opt, oIdx) => (
                              <div key={`${q.question_id || idx}-opt-${oIdx}`} className="flex items-center gap-1">
                                <Input value={opt} onChange={e => updateOption(idx, oIdx, e.target.value)}
                                  placeholder={`Option ${oIdx + 1}`} className="input-zuriya text-xs flex-1" />
                                <button onClick={() => removeOption(idx, oIdx)} className="p-1 text-red-400 hover:text-red-600">
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                            <Button variant="ghost" size="sm" onClick={() => addOption(idx)} className="text-xs text-[#D4AF37]">
                              + Add Option
                            </Button>
                          </div>
                        )}
                      </div>
                      {questions.length > 1 && (
                        <button onClick={() => removeQuestion(idx)} className="p-1 text-red-400 hover:text-red-600 mt-0.5">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addQuestion} className="mt-2 gap-1 text-xs" data-testid="add-question-btn">
                <Plus className="h-3 w-3" /> Add Question
              </Button>
            </div>

            {services.length > 0 && (
              <div>
                <Label className="mb-2 block">Assign to Services (optional)</Label>
                <p className="text-xs text-stone-400 mb-2">Leave unchecked to use for all services</p>
                <div className="space-y-2 max-h-36 overflow-y-auto">
                  {services.map(svc => (
                    <label key={svc.service_id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={formServiceIds.includes(svc.service_id)} onCheckedChange={() => toggleFormService(svc.service_id)} />
                      <span>{svc.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleSave} className="btn-primary w-full" data-testid="save-form-btn">
              {editingForm ? 'Update Form' : 'Create Form'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
